import type { PlatformWorkbookBucketId } from './workbook.types'

export interface WorkbookFieldCatalogEntry {
    id: string
    /** Default EZOS section home — definition placement can override. */
    bucket: PlatformWorkbookBucketId
    label: string
    input_type: string
    fha_tier: 'safe' | 'caution' | 'prohibited'
}

export const WORKBOOK_BUCKET_LABELS: Record<
    PlatformWorkbookBucketId,
    string
> = {
    identity_contact: 'Identity & Contact',
    lease_preferences: 'Lease Preferences',
    financial: 'Qualification',
    failed_qualification: 'Failed Qualification',
    operational: 'Add-Ons and Emergency Contacts',
    lifestyle: 'Lifestyle & Roommates',
}

export const WORKBOOK_BUCKET_SUMMARIES: Record<
    PlatformWorkbookBucketId,
    string
> = {
    identity_contact:
        'Legal name, gender, contact info, current address, and school.',
    lease_preferences:
        'Move-in, move-out, floor plan, premiums, and roommates.',
    financial:
        'How the applicant will qualify, plus guarantor identity if they choose a guarantor.',
    failed_qualification:
        'After-fail qualifying methods when the applicant chooses among them.',
    operational: 'Emergency contact, parking, and pets.',
    lifestyle: 'Roommate compatibility and living preferences.',
}

export const WORKBOOK_BUCKET_ORDER: PlatformWorkbookBucketId[] = [
    'identity_contact',
    'lease_preferences',
    'financial',
    'failed_qualification',
    'operational',
    'lifestyle',
]

export const WORKBOOK_FIELD_CATALOG: WorkbookFieldCatalogEntry[] = [
    {
        id: 'legal_first_name',
        bucket: 'identity_contact',
        label: 'What is your first name as it appears on a government ID?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'legal_middle_name',
        bucket: 'identity_contact',
        label: 'What is your middle name as it appears on a government ID?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'no_legal_middle_name',
        bucket: 'identity_contact',
        label: 'I certify I do not have a legal middle name.',
        input_type: 'boolean',
        fha_tier: 'safe',
    },
    {
        id: 'legal_last_name',
        bucket: 'identity_contact',
        label: 'What is your last name as it appears on a government ID?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'date_of_birth',
        bucket: 'identity_contact',
        label: 'What is your date of birth?',
        input_type: 'date',
        fha_tier: 'safe',
    },
    {
        id: 'current_address',
        bucket: 'identity_contact',
        label: 'What is your current address?',
        input_type: 'address',
        fha_tier: 'safe',
    },
    {
        id: 'phone',
        bucket: 'identity_contact',
        label: 'What is your phone number?',
        input_type: 'phone',
        fha_tier: 'safe',
    },
    {
        id: 'gender',
        bucket: 'identity_contact',
        label: 'What is your gender as it appears on a government-issued ID?',
        input_type: 'select',
        fha_tier: 'caution',
    },
    {
        id: 'ssn',
        bucket: 'identity_contact',
        label: 'Social Security number',
        input_type: 'ssn',
        fha_tier: 'safe',
    },
    {
        id: 'current_address_line1',
        bucket: 'identity_contact',
        label: 'Street address',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'current_address_city',
        bucket: 'identity_contact',
        label: 'City',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'current_address_state',
        bucket: 'identity_contact',
        label: 'State',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'current_address_zip',
        bucket: 'identity_contact',
        label: 'ZIP code',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'government_id_upload',
        bucket: 'identity_contact',
        label: 'Government ID upload',
        input_type: 'file',
        fha_tier: 'safe',
    },
    {
        id: 'university_email',
        bucket: 'identity_contact',
        label: 'University email',
        input_type: 'email',
        fha_tier: 'safe',
    },
    {
        id: 'university',
        bucket: 'identity_contact',
        label: 'What school are you attending?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'graduation_year',
        bucket: 'identity_contact',
        label: 'What is your expected graduation year?',
        input_type: 'number',
        fha_tier: 'safe',
    },
    {
        id: 'lease_term',
        bucket: 'lease_preferences',
        label: 'Lease term',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'preferred_move_in_date',
        bucket: 'lease_preferences',
        label: 'What is your preferred move-in date?',
        input_type: 'date',
        fha_tier: 'safe',
    },
    {
        id: 'preferred_move_out_date',
        bucket: 'lease_preferences',
        label: 'What is your preferred move-out date?',
        input_type: 'date',
        fha_tier: 'safe',
    },
    {
        id: 'floor_plan_preference',
        bucket: 'lease_preferences',
        label: 'Which floor plan are you interested in?',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'preferred_premiums',
        bucket: 'lease_preferences',
        label: 'Are you interested in a premium room?',
        input_type: 'premiums',
        fha_tier: 'safe',
    },
    {
        id: 'preferred_roommates',
        bucket: 'lease_preferences',
        label: 'Would you like to add any preferred roommates?',
        input_type: 'roommates',
        fha_tier: 'safe',
    },
    {
        id: 'comfortable_with_roommate_pets',
        bucket: 'lease_preferences',
        label: 'Are you comfortable living with a roommate who has a pet?',
        input_type: 'boolean',
        fha_tier: 'safe',
    },
    {
        id: 'qualifying_method',
        bucket: 'financial',
        label: 'How do you want to qualify?',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'guarantor_first_name',
        bucket: 'financial',
        label:
            "What is the guarantor's first name as it appears on a government ID?",
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'guarantor_middle_name',
        bucket: 'financial',
        label:
            "What is the guarantor's middle name as it appears on a government ID?",
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'no_guarantor_middle_name',
        bucket: 'financial',
        label: 'I certify the guarantor does not have a legal middle name.',
        input_type: 'boolean',
        fha_tier: 'safe',
    },
    {
        id: 'guarantor_last_name',
        bucket: 'financial',
        label:
            "What is the guarantor's last name as it appears on a government ID?",
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'guarantor_email',
        bucket: 'financial',
        label: "What is the guarantor's email address?",
        input_type: 'email',
        fha_tier: 'safe',
    },
    {
        id: 'guarantor_phone',
        bucket: 'financial',
        label: "What is the guarantor's phone number?",
        input_type: 'phone',
        fha_tier: 'safe',
    },
    {
        id: 'guarantor_relationship',
        bucket: 'financial',
        label: "What is the guarantor's relationship to you?",
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'fallback_qualifying_method',
        bucket: 'failed_qualification',
        label:
            'You did not pass qualification. Which method would you like to use?',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'emergency_contact_name',
        bucket: 'operational',
        label: 'Emergency contact name',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'emergency_contact_phone',
        bucket: 'operational',
        label: 'Emergency contact phone',
        input_type: 'phone',
        fha_tier: 'safe',
    },
    {
        id: 'emergency_contact_access',
        bucket: 'operational',
        label: 'May emergency contact access unit?',
        input_type: 'boolean',
        fha_tier: 'safe',
    },
    {
        id: 'parking_needed',
        bucket: 'operational',
        label: 'Do you need parking?',
        input_type: 'boolean',
        fha_tier: 'safe',
    },
    {
        id: 'vehicle_make',
        bucket: 'operational',
        label: 'What is your vehicle make?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'vehicle_model',
        bucket: 'operational',
        label: 'What is your vehicle model?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'vehicle_color',
        bucket: 'operational',
        label: 'What is your vehicle color?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'vehicle_license_plate',
        bucket: 'operational',
        label: 'What is your license plate number?',
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'vehicle_license_plate_state',
        bucket: 'operational',
        label: 'What is your license plate state?',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'has_pets',
        bucket: 'operational',
        label: 'Will a pet live with you in the unit?',
        input_type: 'boolean',
        fha_tier: 'safe',
    },
    {
        id: 'pet_animal_type',
        bucket: 'operational',
        label: 'What type of animal will live with you?',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'pet_breed',
        bucket: 'operational',
        label: 'What breed is the dog?',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'pet_name',
        bucket: 'operational',
        label: "What is the pet's name?",
        input_type: 'text',
        fha_tier: 'safe',
    },
    {
        id: 'pet_weight',
        bucket: 'operational',
        label: "What is the pet's weight?",
        input_type: 'number',
        fha_tier: 'safe',
    },
    {
        id: 'pet_picture',
        bucket: 'operational',
        label: 'Upload a picture of your pet',
        input_type: 'file',
        fha_tier: 'safe',
    },
    {
        id: 'study_habits',
        bucket: 'lifestyle',
        label: 'Study habits',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'sleep_schedule',
        bucket: 'lifestyle',
        label: 'Sleep schedule',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'cleanliness',
        bucket: 'lifestyle',
        label: 'Cleanliness',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'noise_tolerance',
        bucket: 'lifestyle',
        label: 'Noise tolerance',
        input_type: 'select',
        fha_tier: 'safe',
    },
    {
        id: 'social_preference',
        bucket: 'lifestyle',
        label: 'Social preference',
        input_type: 'select',
        fha_tier: 'safe',
    },
]

/** EZOS registry fields that must remain placed in every workbook. */
export const PINNED_WORKBOOK_FIELD_IDS = new Set([
    'legal_first_name',
    'legal_middle_name',
    'no_legal_middle_name',
    'legal_last_name',
    'date_of_birth',
    'current_address',
    'phone',
    'preferred_move_in_date',
    'preferred_move_out_date',
    'floor_plan_preference',
    'qualifying_method',
    'guarantor_first_name',
    'guarantor_middle_name',
    'no_guarantor_middle_name',
    'guarantor_last_name',
    'guarantor_email',
    'guarantor_phone',
    'guarantor_relationship',
    'emergency_contact_name',
    'emergency_contact_phone',
    'emergency_contact_access',
    'parking_needed',
    'has_pets',
    'study_habits',
    'sleep_schedule',
    'cleanliness',
    'noise_tolerance',
    'social_preference',
])

/** Required in the default template, but removable (not pinned). */
export const DEFAULT_REQUIRED_WORKBOOK_FIELD_IDS = new Set([
    'gender',
    'comfortable_with_roommate_pets',
    'vehicle_make',
    'vehicle_model',
    'vehicle_color',
    'vehicle_license_plate',
    'vehicle_license_plate_state',
    'pet_animal_type',
    'pet_breed',
    'pet_name',
    'pet_weight',
    'qualifying_method',
    'fallback_qualifying_method',
])

/** Stay in the EZOS catalog so they can be added, but do not auto-place. */
export const DEFAULT_UNPLACED_WORKBOOK_FIELD_IDS = new Set([
    'lease_term',
    'employment_employer',
    'employment_income',
    'income_proof_upload',
    'financial_aid_upload',
    'vehicle_info',
    'pet_details',
    'renters_insurance',
    'academic_focus',
])

export const GUARANTOR_DEPENDENT_FIELD_IDS = new Set([
    'guarantor_first_name',
    'guarantor_middle_name',
    'no_guarantor_middle_name',
    'guarantor_last_name',
    'guarantor_email',
    'guarantor_phone',
    'guarantor_relationship',
])

export const GUARANTOR_FIELDS_SHOW_IF = {
    depends_on_field_id: 'qualifying_method',
    equals: 'personal_guarantor',
}

export const VEHICLE_DEPENDENT_FIELD_IDS = new Set([
    'vehicle_make',
    'vehicle_model',
    'vehicle_color',
    'vehicle_license_plate',
    'vehicle_license_plate_state',
])

export const VEHICLE_FIELDS_SHOW_IF = {
    depends_on_field_id: 'parking_needed',
    equals: 'Yes',
}

export const PET_HAS_PETS_DEPENDENT_FIELD_IDS = new Set([
    'pet_animal_type',
    'pet_name',
    'pet_weight',
    'pet_picture',
])

export function catalogFieldsForBucket(
    bucketId: PlatformWorkbookBucketId,
): WorkbookFieldCatalogEntry[] {
    return WORKBOOK_FIELD_CATALOG.filter((field) => field.bucket === bucketId)
}

/** Short card titles. The applicant-facing prompt stays on `label`. */
const WORKBOOK_FIELD_TITLES: Record<string, string> = {
    legal_first_name: 'Legal first name',
    legal_middle_name: 'Legal middle name',
    no_legal_middle_name: 'No legal middle name',
    legal_last_name: 'Legal last name',
    date_of_birth: 'Date of birth',
    current_address: 'Current address',
    phone: 'Phone',
    gender: 'Gender',
    ssn: 'Social Security number',
    current_address_line1: 'Street address',
    current_address_city: 'City',
    current_address_state: 'State',
    current_address_zip: 'ZIP code',
    government_id_upload: 'Government ID',
    university_email: 'University email',
    university: 'School',
    graduation_year: 'Graduation year',
    lease_term: 'Lease term',
    preferred_move_in_date: 'Move-in date',
    preferred_move_out_date: 'Move-out date',
    floor_plan_preference: 'Floor plan',
    preferred_premiums: 'Premium room',
    preferred_roommates: 'Roommates',
    comfortable_with_roommate_pets: 'Roommate pets',
    qualifying_method: 'Qualifying method',
    guarantor_first_name: 'Guarantor first name',
    guarantor_middle_name: 'Guarantor middle name',
    no_guarantor_middle_name: 'No guarantor middle name',
    guarantor_last_name: 'Guarantor last name',
    guarantor_email: 'Guarantor email',
    guarantor_phone: 'Guarantor phone',
    guarantor_relationship: 'Guarantor relationship',
    fallback_qualifying_method: 'Alternate qualifying method',
    emergency_contact_name: 'Emergency contact name',
    emergency_contact_phone: 'Emergency contact phone',
    emergency_contact_access: 'Emergency contact access',
    parking_needed: 'Parking',
    vehicle_make: 'Vehicle make',
    vehicle_model: 'Vehicle model',
    vehicle_color: 'Vehicle color',
    vehicle_license_plate: 'License plate',
    vehicle_license_plate_state: 'License plate state',
    has_pets: 'Pet',
    pet_animal_type: 'Animal type',
    pet_breed: 'Dog breed',
    pet_name: 'Pet name',
    pet_weight: 'Pet weight',
    pet_picture: 'Pet picture',
    study_habits: 'Study habits',
    sleep_schedule: 'Sleep schedule',
    cleanliness: 'Cleanliness',
    noise_tolerance: 'Noise tolerance',
    social_preference: 'Social preference',
}

function humanizeFieldId(fieldId: string): string {
    const words = fieldId.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    if (!words) return ''
    return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Card heading for a workbook question. Catalog ids use a fixed short title. */
export function workbookFieldTitle(fieldId: string): string {
    return (
        WORKBOOK_FIELD_TITLES[fieldId] ||
        humanizeFieldId(fieldId) ||
        'Custom question'
    )
}

/** Safe label lookup for platform or custom section ids. */
export function getWorkbookBucketLabel(bucketId: string): string {
    if (
        Object.prototype.hasOwnProperty.call(WORKBOOK_BUCKET_LABELS, bucketId)
    ) {
        return WORKBOOK_BUCKET_LABELS[bucketId as PlatformWorkbookBucketId]
    }
    return bucketId
}
